// wsClient.ts

type MessageHandler<T = unknown> = (payload: T) => void;
type CommandHandler<T = unknown> = (payload: T) => void;

/* =======================
   Message envelopes
   ======================= */

type IncomingTopicMessage<T = unknown> = {
  topic: string;
  payload: T;
  command?: string;
};

type IncomingControlMessage =
  | { type: "pong" }
  | { type: "ack"; command?: string };

type IncomingMessage = IncomingTopicMessage | IncomingControlMessage;

/* =======================
   URL
   ======================= */

const URL =
  process.env.NODE_ENV !== "production"
    ? "ws://localhost:3000/connect"
    : "wss://zeusui.zeus-control.com/connect";

/* =======================
   Client
   ======================= */

class WSClient {
  private ws?: WebSocket;
  private connected = false;

  // topic → handlers
  private topicHandlers = new Map<string, Set<MessageHandler>>();

  // command → handlers
  private commandHandlers = new Map<string, Set<CommandHandler>>();

  // queued outbound messages
  private sendQueue: string[] = [];

  private heartbeatTimer?: number;
  private reconnectTimer?: number;

  private readonly url = URL;
  private readonly heartbeatInterval = 15_000;

  /* =======================
     Connection
     ======================= */

  connect() {
    if (this.ws) return;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.connected = true;
      console.log("[WS] connected");

      // Re-subscribe topics
      for (const topic of this.topicHandlers.keys()) {
        this.sendSubscribe(topic);
      }

      // Flush queue
      for (const msg of this.sendQueue) {
        this.ws!.send(msg);
      }
      this.sendQueue = [];

      this.startHeartbeat();
    };

    this.ws.onclose = () => {
      console.warn("[WS] disconnected");
      this.cleanup();
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      console.error("[WS] error");
      this.ws?.close();
    };

    this.ws.onmessage = (event) => {
      const msg = JSON.parse(event.data) as IncomingMessage;

      // Control messages
      if ("type" in msg) {
        if (msg.type === "pong") return;
        return;
      }

      // Command routing (priority)
      if (msg.command) {
        const handlers = this.commandHandlers.get(msg.command);
        handlers?.forEach((fn) => fn(msg.payload));
        return;
      }

      // Topic routing
      const handlers = this.topicHandlers.get(msg.topic);
      handlers?.forEach((fn) => fn(msg.payload));
    };
  }

  private cleanup() {
    this.connected = false;
    this.ws = undefined;
    clearInterval(this.heartbeatTimer);
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, 2000);
  }

  /* =======================
     Sending (safe)
     ======================= */

  private send(data: object) {
    const msg = JSON.stringify(data);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else {
      this.sendQueue.push(msg);
    }
  }

  /* =======================
     Topics
     ======================= */

  subscribe<T>(topic: string, handler: MessageHandler<T>) {
    if (!this.topicHandlers.has(topic)) {
      this.topicHandlers.set(topic, new Set());
    }

    const handlers = this.topicHandlers.get(topic)!;
    const isFirst = handlers.size === 0;

    handlers.add(handler as MessageHandler);

    if (isFirst) {
      this.sendSubscribe(topic);
    }
  }

  unsubscribe<T>(topic: string, handler: MessageHandler<T>) {
    const handlers = this.topicHandlers.get(topic);
    if (!handlers) return;

    handlers.delete(handler as MessageHandler);

    if (handlers.size === 0) {
      this.topicHandlers.delete(topic);
      this.sendUnsubscribe(topic);
    }
  }

  private sendSubscribe(topic: string) {
    this.send({ action: "subscribe", topic });
  }

  private sendUnsubscribe(topic: string) {
    this.send({ action: "unsubscribe", topic });
  }

  /* =======================
     Commands
     ======================= */

  onCommand<T>(command: string, handler: CommandHandler<T>) {
    if (!this.commandHandlers.has(command)) {
      this.commandHandlers.set(command, new Set());
    }

    this.commandHandlers.get(command)!.add(handler as CommandHandler);
  }

  offCommand<T>(command: string, handler: CommandHandler<T>) {
    this.commandHandlers.get(command)?.delete(handler as CommandHandler);
  }

  sendCommand<T, K>(command: string, payload?: T, meta?: K) {
    this.send({
      action: "command",
      command,
      payload,
      meta,
    });
  }

  /* =======================
     Publish
     ======================= */

  publish<T>(topic: string, payload: T) {
    this.send({
      action: "publish",
      topic,
      payload,
    });
  }

  /* =======================
     Heartbeat
     ======================= */

  private startHeartbeat() {
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: "ping" });
    }, this.heartbeatInterval);
  }
}

export const wsClient = new WSClient();
